import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';

// Design System
import { TertiaryButton } from 'jl-design-system/elements/button/Button';
import { Message } from 'jl-design-system/components/message/Message';
import { resetForm } from 'jl-design-system/redux/actions/form/formActions';
import { LoadingSpinner } from 'jl-design-system/components/loading-spinner/LoadingSpinner';

// Config
import paymentTypeConstants from '@constants/paymentTypeConstants';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import { applyGiftCard, removeGiftOption } from '@redux/actions/payment/applyGiftCardActions';
import { applyGiftVoucher } from '@redux/actions/payment/applyGiftVoucherAction';
import {
  closeGiftOptionsModal,
  openGiftOptionsModal,
  changeGiftOption,
  redirectFromGiftCardToPayment,
} from '@redux/actions/payment/paymentActions';
import { applyPromoCode, removePromoCode } from '@redux/actions/payment/promoCodeActions';
import { resetSubmitOrderError } from '@redux/actions/payment/submitOrderActions';
import { giftCardsSelector } from '@redux/reducers/payment/giftCardReducer';
import { getGiftOptionWithRemainingBalance } from '@redux/reducers/payment/giftOptionsHigherOrderReducer';
import { giftVouchersSelector } from '@redux/reducers/payment/giftVoucherReducer';
import { isZeroAmount } from '@utils/index';
import { APPLY_GIFT_CARD_FORM_ID, APPLY_GIFT_VOUCHER_FORM_ID } from '@utils/form/configs/giftOptions';
import { PROMO_CODE_FORM_ID } from '@utils/form/configs/promoCode';
import { isEmptyObject } from '@utils/object';

// Components
import PaymentDeductionsList from './PaymentDeductionsList';
import { GiftOptionsModal } from './gift-options-modal';
import PromoCodeForm from './promo-code-form';
import { BillingDetails } from '@components/add-payment-details/card-details/billing-details';
import Container from '@components/container';
import Heading from '@components/heading';
import RewardsPromoWarning from '@components/rewards-promo-warning/RewardsPromoWarning';

// Styles
import styles from './payment-deductions.scss';

export class PaymentDeductions extends PureComponent {
  static propTypes = {
    anyGiftOptionsApplied: PropTypes.bool,
    appliedOptionsConfig: PropTypes.arrayOf(PropTypes.object),
    applyGiftCard: PropTypes.func,
    applyGiftPaymentFailed: PropTypes.bool,
    applyGiftVoucher: PropTypes.func,
    applyPromoCode: PropTypes.func,
    applyPromoCodeSubmitFailed: PropTypes.bool,
    applyPromoCodeSubmitting: PropTypes.bool,
    changeGiftOption: PropTypes.func,
    closeGiftOptionsModal: PropTypes.func,
    disabled: PropTypes.bool,
    getPaymentPageComplete: PropTypes.bool,
    giftApplicationApiCallIsActive: PropTypes.bool,
    isClickCollectOrder: PropTypes.bool,
    isGuest: PropTypes.bool,
    modalContentId: PropTypes.string,
    modalOpen: PropTypes.bool,
    openGiftOptionsModal: PropTypes.func,
    outstandingBalance: PropTypes.string,
    promoCodeApplied: PropTypes.shape({}),
    promoCodeError: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        body: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        title: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.array.isRequired]),
      }),
    ]),
    promoCodeSubmissionError: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        body: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        title: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.array.isRequired]),
      }),
    ]),
    redirectFromGiftCardToPayment: PropTypes.func,
    removeGiftOption: PropTypes.func,
    removePromoCode: PropTypes.func,
    resetForm: PropTypes.func,
    resetSubmitOrderError: PropTypes.func,
    rewardApplied: PropTypes.bool,
    showRemovePromoCodeErrorMessage: PropTypes.bool,
    silentlyAppliedPromoCode: PropTypes.bool,
    submitOrderError: PropTypes.shape({}),
    triggerAnalyticsEvent: PropTypes.func,
  };

  static defaultProps = {
    anyGiftOptionsApplied: false,
    appliedOptionsConfig: [],
    applyGiftCard: window.defaultFunc,
    applyGiftPaymentFailed: false,
    applyGiftVoucher: window.defaultFunc,
    applyPromoCode: window.defaultFunc,
    applyPromoCodeSubmitFailed: false,
    applyPromoCodeSubmitting: false,
    changeGiftOption: window.defaultFunc,
    closeGiftOptionsModal: window.defaultFunc,
    disabled: false,
    getPaymentPageComplete: false,
    giftApplicationApiCallIsActive: false,
    isClickCollectOrder: false,
    isGuest: false,
    modalContentId: '',
    modalOpen: false,
    openGiftOptionsModal: window.defaultFunc,
    outstandingBalance: '',
    promoCodeApplied: undefined,
    promoCodeError: '',
    promoCodeSubmissionError: undefined,
    redirectFromGiftCardToPayment: window.defaultFunc,
    removeGiftOption: window.defaultFunc,
    removePromoCode: window.defaultFunc,
    resetForm: window.defaultFunc,
    resetSubmitOrderError: window.defaultFunc,
    rewardApplied: false,
    showRemovePromoCodeErrorMessage: false,
    silentlyAppliedPromoCode: false,
    submitOrderError: undefined,
    triggerAnalyticsEvent: window.defaultFunc,
  };

  static getDerivedStateFromProps(props, state) {
    const { applyPromoCodeSubmitFailed, applyPromoCodeSubmitting } = props;

    if (state.applyPromoCodeSubmitting && !applyPromoCodeSubmitting) {
      if (!applyPromoCodeSubmitFailed) {
        return {
          showForm: false,
          applyPromoCodeSubmitting,
        };
      }
    }

    return {
      applyPromoCodeSubmitting,
    };
  }

  constructor() {
    super();

    this.state = {
      showForm: false,
      showWarningModal: false,
      promoCodeLoadingCancelled: false,
    };
  }

  onShowModalButtonClick = () => {
    const { openGiftOptionsModal, submitOrderError, resetSubmitOrderError } = this.props;

    if (submitOrderError) {
      resetSubmitOrderError();
    }

    openGiftOptionsModal();
  };

  onModalClose = () => {
    const { closeGiftOptionsModal, resetForm } = this.props;
    closeGiftOptionsModal();
    resetForm(APPLY_GIFT_CARD_FORM_ID);
    resetForm(APPLY_GIFT_VOUCHER_FORM_ID);
    resetForm(PROMO_CODE_FORM_ID);
  };

  onRemoveGiftOption = (item, type) => {
    if (type === paymentTypeConstants.PROMO_CODE) {
      this.props.removePromoCode(item);
    } else {
      this.props.removeGiftOption(item);
    }
  };

  onUndoGiftOptionRemove = (item, type) => {
    const { applyGiftCard, applyGiftVoucher, applyPromoCode, rewardApplied } = this.props;

    if (type === paymentTypeConstants.GIFT_CARD) {
      const {
        details: { cardNumber, pin },
      } = item;
      applyGiftCard({ cardNumber, pin, isUndo: true });
    }

    if (type === paymentTypeConstants.GIFT_VOUCHER) {
      const {
        details: { serialNumber, securityCode },
      } = item;
      applyGiftVoucher({ securityCode, serialNumber, isUndo: true });
    }

    if (type === paymentTypeConstants.PROMO_CODE) {
      const { id } = item;
      if (rewardApplied) {
        this.setState({
          showWarningModal: true,
          promoCodeLoadingCancelled: false,
          promoCodeId: id,
        });
        return;
      }

      applyPromoCode({ promoCode: id, isUndo: true });
    }
  };

  onShowButtonClick = () => {
    this.setState({
      showForm: true,
    });
  };

  renderUseGiftOptionsButton = () => {
    const { disabled, outstandingBalance } = this.props;

    const orderTotalCovered = isZeroAmount(outstandingBalance);
    const isDisabled = disabled || orderTotalCovered;

    const buttonClassnames = classNames(styles.giftOptionsCta, {
      [styles.giftOptionsCtaDisabled]: isDisabled,
    });
    const iconClassnames = classNames(styles.giftOptionsCtaIcon, {
      [styles.giftOptionsCtaIconDisabled]: isDisabled,
    });

    return (
      <>
        <button
          className={buttonClassnames}
          data-testid="gift-options-toggle-button"
          disabled={isDisabled}
          onClick={orderTotalCovered ? () => {} : this.onShowModalButtonClick}
          type="button"
        >
          <span className={iconClassnames} />
          <span>Use gift card or voucher</span>
        </button>
      </>
    );
  };

  render() {
    const {
      anyGiftOptionsApplied,
      appliedOptionsConfig,
      applyGiftPaymentFailed,
      applyPromoCode,
      changeGiftOption,
      disabled,
      getPaymentPageComplete,
      giftApplicationApiCallIsActive,
      isClickCollectOrder,
      isGuest,
      modalContentId,
      modalOpen,
      outstandingBalance,
      promoCodeApplied,
      promoCodeError,
      promoCodeSubmissionError,
      showRemovePromoCodeErrorMessage,
      silentlyAppliedPromoCode,
      redirectFromGiftCardToPayment,
      rewardApplied,
    } = this.props;

    const { showForm, showWarningModal, promoCodeId, promoCodeLoadingCancelled } = this.state;

    const containerClassnames = classNames(styles.giftOptionsContainer);
    const appliedContainerClassnames = classNames(styles.giftOptionsAppliedContainer);

    if (!outstandingBalance) {
      return null;
    }

    if (!getPaymentPageComplete) {
      return (
        <Container className={styles.spinnerContainer} marginBottom="3">
          <LoadingSpinner />
        </Container>
      );
    }

    const orderTotalCovered = isZeroAmount(outstandingBalance);
    const appliedPromoCode = appliedOptionsConfig.find((option) => option.type === 'promoCode');
    const promoCodeAppliedAmount = appliedPromoCode?.list?.[0]?.amount;

    return (
      <div className={containerClassnames}>
        {showForm ? (
          <PromoCodeForm onSubmitForm={applyPromoCode} promoCodeError={promoCodeError} rewardApplied={rewardApplied} />
        ) : (
          <TertiaryButton
            className={styles.promoCodeCta}
            data-testid="apply-promotion-toggle-button"
            disabled={disabled}
            onClick={this.onShowButtonClick}
            small
          >
            Add a promo code
          </TertiaryButton>
        )}

        {anyGiftOptionsApplied && this.renderUseGiftOptionsButton()}

        {showRemovePromoCodeErrorMessage && !modalOpen && (
          <Container marginTop="3">
            <Message
              body="Please try again. If the problem persists, please try again later."
              data-testid="remove-promo-error"
              title="Sorry, there was a technical problem with removing your promo code"
              type="error"
            />
          </Container>
        )}

        {silentlyAppliedPromoCode && promoCodeAppliedAmount && (
          <Container marginTop="3">
            <Message
              body={`We automatically removed ${promoCodeAppliedAmount} from this order`}
              title="Enjoy your savings!"
              type="information"
            />
          </Container>
        )}

        {promoCodeSubmissionError && (
          <Container marginTop="3">
            <Message
              dataId="promo-code-submission-error"
              scrollToMessageOnMount
              title={promoCodeSubmissionError}
              type="error"
            />
          </Container>
        )}

        {(anyGiftOptionsApplied || promoCodeApplied) && (
          <Container
            border="black"
            className={appliedContainerClassnames}
            marginTop="3"
            testId="gift-option-applied-section"
          >
            <div className={styles.giftOptionsAppliedDeductionsContainer}>
              {appliedOptionsConfig.map((props) => (
                <PaymentDeductionsList
                  key={props.title}
                  applyGiftPaymentFailed={applyGiftPaymentFailed}
                  {...props}
                  isGuest={isGuest}
                  onRemove={this.onRemoveGiftOption}
                  onUndo={this.onUndoGiftOptionRemove}
                  orderTotalCovered={orderTotalCovered}
                  promoCodeLoadingCancelled={promoCodeLoadingCancelled}
                  showUndo
                />
              ))}
            </div>
            <Heading tag="div" type="s">
              <Container
                borderTop="grey-15"
                className={styles.balance}
                paddingTop="2"
                testId="gift-options-list-balance"
              >
                <span>Balance to pay</span>
                <span>{outstandingBalance}</span>
              </Container>
            </Heading>
          </Container>
        )}

        {!anyGiftOptionsApplied && this.renderUseGiftOptionsButton()}

        <GiftOptionsModal
          giftApplicationApiCallIsActive={giftApplicationApiCallIsActive}
          handleModalClose={this.onModalClose}
          isOpen={modalOpen}
          modalContentId={modalContentId}
          onOptionChange={changeGiftOption}
          onRemove={this.onRemoveGiftOption}
          onUndo={this.onUndoGiftOptionRemove}
          redirectFromGiftCardToPayment={redirectFromGiftCardToPayment}
        />

        {orderTotalCovered && (
          <Container marginTop="3">
            <Message
              dataTestId="no-further-payment-message"
              title="No further payment is required"
              type="information"
            />
          </Container>
        )}

        {isClickCollectOrder && orderTotalCovered && (
          <Container marginTop="5" testId="billing-details-container">
            <Heading label="Your contact details" marginBottom="2" tag="h2" type="s" />
            <p>Please let us know who to contact in case there are any problems with your order</p>
            <BillingDetails hideTitle />
          </Container>
        )}

        {showWarningModal && (
          <RewardsPromoWarning
            direction="promo-to-reward"
            onCancel={() =>
              this.setState({
                showWarningModal: false,
                promoCodeLoadingCancelled: true,
              })}
            onContinue={() => {
              applyPromoCode({ promoCode: promoCodeId });
              this.setState({ showWarningModal: false });
            }}
          />
        )}
      </div>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const outstandingBalance = state.orderForm?.amounts?.outstandingBalance;
  const { usedGiftCards: giftCards = [], unusedGiftCards } = giftCardsSelector(state, ownProps.renderingContext);
  const giftVouchers = giftVouchersSelector(state?.orderForm?.payments, ownProps.renderingContext);

  const { promoCodeApplied } = state.orderForm ?? {};
  const isPromoCodeApplied = !isEmptyObject(promoCodeApplied ?? {});
  const anyGiftOptionsApplied = giftCards.length > 0 || giftVouchers.length > 0;
  const isGuest = state.user?.isGuest;

  const { cardNumber = '', remainingBalance: giftCardRemainingBalance = '' } = getGiftOptionWithRemainingBalance(
    state,
    paymentTypeConstants.GIFT_CARD,
  );

  const { serialNumber = '', remainingBalance: giftVoucherRemainingBalance = '' } = getGiftOptionWithRemainingBalance(
    state,
    paymentTypeConstants.GIFT_VOUCHER,
  );

  const commonProps = {
    balance: outstandingBalance,
    showRemaining: true,
    // TODO what is invisibleBalance for?
    invisibleBalance: true,
    animationDisabled: true,
    isRemovable: true,
  };

  const appliedOptionsConfig = [
    {
      list: giftCards,
      remainingBalance: giftCardRemainingBalance,
      remainingBalanceCardNumber: cardNumber,
      title: 'Gift cards applied',
      type: paymentTypeConstants.GIFT_CARD,
      unusedGiftCards,
      ...commonProps,
    },
    {
      list: giftVouchers,
      remainingBalance: giftVoucherRemainingBalance,
      remainingBalanceCardNumber: serialNumber,
      title: 'Gift vouchers applied',
      infoMessage: null,
      type: paymentTypeConstants.GIFT_VOUCHER,
      ...commonProps,
    },
  ];

  if (isPromoCodeApplied) {
    appliedOptionsConfig.unshift({
      list: [promoCodeApplied],
      title: 'Promo code applied',
      infoMessage: null,
      ...commonProps,
      type: paymentTypeConstants.PROMO_CODE,
      isRemovable: true,
    });
  }

  const giftApplicationApiCallIsActive =
    state.giftCard?.isLoading ||
    state.giftVoucher?.isLoading ||
    state.orderForm?.removeGiftOptionLoading ||
    state.orderForm?.promoCodeApplied?.isLoading;

  return {
    amounts: state.orderForm.amounts,
    anyGiftOptionsApplied,
    appliedOptionsConfig,
    applyPromoCodeSubmitFailed: state.orderForm.applyPromoCodeFailed,
    applyPromoCodeSubmitting: state.orderForm.applyPromoCodeLoading,
    getPaymentPageComplete: state.orderForm.getPaymentPageComplete,
    giftApplicationApiCallIsActive,
    isClickCollectOrder: state.orderForm.isClickCollectOrder,
    isGuest,
    modalContentId: state.payment.giftOptionsModalContentId,
    modalOpen: state.payment.giftOptionsModalOpen,
    outstandingBalance,
    promoCodeApplied,
    promoCodeError: state.payment.promoCodeError,
    promoCodeSubmissionError: state.payment.promoCodeSubmissionError,
    rewardApplied: !!state.orderForm.amounts?.rewardsDiscountTotal,
    showRemovePromoCodeErrorMessage: state.orderForm.removePromoCodeFailed,
    silentlyAppliedPromoCode: state.orderForm.silentlyAppliedPromoCode,
    submitOrderError: state.payment.submitOrderError,
  };
}

export function mapDispatchToProps() {
  return {
    applyGiftCard,
    applyGiftVoucher,
    applyPromoCode,
    changeGiftOption,
    closeGiftOptionsModal,
    openGiftOptionsModal,
    removeGiftOption,
    removePromoCode,
    resetForm,
    resetSubmitOrderError,
    triggerAnalyticsEvent,
    redirectFromGiftCardToPayment,
  };
}

export default connect((state, ownProps) => mapStateToProps(state, ownProps), mapDispatchToProps())(PaymentDeductions);
